import i18n from "@/locales";
import api from "@/modules/api";
import { Media } from "@/types/entity";

// 获取所有帧的URL列表
export const getAllFrameUrls = async (
  url: string,
  splitWidth: number,
  imageWidth: number,
  imageHeight: number
): Promise<string[]> => {
  const totalFrames = Math.floor(imageWidth / splitWidth);
  const frameUrls: string[] = [];

  // 创建临时图片元素
  const img = new Image();
  img.crossOrigin = "anonymous"; // 处理跨域问题

  return new Promise((resolve, reject) => {
    img.onload = () => {
      try {
        // 创建canvas
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          reject(new Error(i18n.global.t("Message.cannotGetCanvasContext")));
          return;
        }

        // 设置canvas尺寸为单帧尺寸
        canvas.width = splitWidth;
        canvas.height = imageHeight;

        // 为每一帧创建URL
        for (let i = 0; i < totalFrames; i++) {
          // 清空canvas
          ctx.clearRect(0, 0, canvas.width, canvas.height);

          // 绘制当前帧
          ctx.drawImage(
            img,
            i * splitWidth, // 源图片x位置
            0, // 源图片y位置
            splitWidth, // 源图片宽度
            imageHeight, // 源图片高度
            0, // 目标x位置
            0, // 目标y位置
            splitWidth, // 目标宽度
            imageHeight // 目标高度
          );

          // 转换为blob URL
          canvas.toBlob((blob) => {
            if (blob) {
              const url = URL.createObjectURL(blob);
              frameUrls.push(url);

              // 如果所有帧都处理完了，返回结果
              if (frameUrls.length === totalFrames) {
                resolve(frameUrls);
              }
            }
          }, "image/png");
        }
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error(i18n.global.t("Message.imageLoadFailed")));
    };

    img.src = url;
  });
};

/**
 * 从576x256像素的图片中提取第三行第一列的64x64小图
 * @param url 图片URL
 * @returns Promise<string> 返回blob协议的图片URL
 */
export const extractFrameFromSprite = (url: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.crossOrigin = "anonymous";

    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");

        const ctx = canvas.getContext("2d");

        if (!ctx) {
          reject(new Error(i18n.global.t("Message.cannotGetCanvasContext")));
          return;
        }

        // 设置canvas尺寸为64x64
        canvas.width = 64;
        canvas.height = 64;

        // 计算第三行第一列的位置
        // 第三行 = 第2行（0-based索引），第一列 = 第0列
        const sourceX = 0 * 64; // 第一列
        const sourceY = 2 * 64; // 第三行

        // 绘制指定区域到canvas
        ctx.drawImage(
          img,
          sourceX, // 源图片x位置
          sourceY, // 源图片y位置
          64, // 源图片宽度
          64, // 源图片高度
          0, // 目标x位置
          0, // 目标y位置
          64, // 目标宽度
          64 // 目标高度
        );

        // 转换为blob URL
        canvas.toBlob((blob) => {
          if (blob) {
            const blobUrl = URL.createObjectURL(blob);
            resolve(blobUrl);
          } else {
            reject(new Error(i18n.global.t("Message.cannotCreateBlob")));
          }
        }, "image/png");
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error(i18n.global.t("Message.imageLoadFailed")));
    };

    img.src = url;
  });
};

/**
 * 从192x96图片中裁剪出48x96的图片，返回第一张的blob URL
 * @param url 图片URL
 * @returns Promise<string> 第一张图片的blob URL
 */
export const cropFirstFrameFrom192 = (url: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          reject(new Error(i18n.global.t("Message.cannotGetCanvasContext")));
          return;
        }

        // 设置canvas尺寸为48x96
        canvas.width = 48;
        canvas.height = 96;

        // 计算第一张图片的位置（第一列）
        const sourceX = 0 * 48; // 第一列
        const sourceY = 0; // 从顶部开始

        // 绘制指定区域到canvas
        ctx.drawImage(
          img,
          sourceX, // 源图片x位置
          sourceY, // 源图片y位置
          48, // 源图片宽度
          96, // 源图片高度
          0, // 目标x位置
          0, // 目标y位置
          48, // 目标宽度
          96 // 目标高度
        );

        // 转换为blob URL
        canvas.toBlob((blob) => {
          if (blob) {
            const blobUrl = URL.createObjectURL(blob);
            resolve(blobUrl);
          } else {
            reject(new Error(i18n.global.t("Message.cannotCreateBlob")));
          }
        }, "image/png");
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error(i18n.global.t("Message.imageLoadFailed")));
    };

    img.src = url;
  });
};

export const uploadMediaFromUrl = async (
  url: string,
  uid: number
): Promise<Media> => {
  const portraitResponse = await fetch(url);
  const portraitBlob = await portraitResponse.blob();

  // 根据实际的 MIME 类型确定文件扩展名
  const mimeType = portraitBlob.type;
  let extension = "png";
  let fileType = "image/png";

  if (mimeType === "image/jpeg") {
    extension = "jpg";
    fileType = "image/jpeg";
  } else if (mimeType === "image/webp") {
    extension = "webp";
    fileType = "image/webp";
  }

  const portraitFile = new File([portraitBlob], `${Date.now()}.${extension}`, {
    type: fileType,
  });

  const { item } = await api.uploadMedia(0, portraitFile, uid);

  return item;
};
